import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AGE_BAND_PRESETS,
  AGE_BAND_TYPE_LABELS,
  parseAgeBandStart,
  type AgeBandType,
} from "@/lib/ageBands";
import {
  getLifeDisabilityRateDefaults,
  RATE_BASIS_LABELS,
  RATE_TYPE_LABELS,
  type LifeDisabilityRateBasis,
  type LifeDisabilityRateType,
} from "@/lib/lifeDisabilityRateDefaults";

// Mirrors WizardRateMode + WizardRateTable in server/lib/easeImport/types.ts —
// kept local rather than imported to avoid dragging server types into the
// client bundle.

export type WizardRateMode =
  | "tier_rates"
  | "age_tier_rates"
  | "volume_per_thousand";

type Tier =
  | "employee_only"
  | "employee_spouse"
  | "employee_children"
  | "family";

const TIERS: { key: Tier; label: string }[] = [
  { key: "employee_only", label: "EE Only" },
  { key: "employee_spouse", label: "EE + Spouse" },
  { key: "employee_children", label: "EE + Children" },
  { key: "family", label: "Family" },
];

export interface TierRateEntry {
  tier: Tier;
  monthlyRate: number | null;
}
export interface AgeTierRateEntry {
  ageBand: string;
  tier: Tier;
  monthlyRate: number | null;
}
export interface VolumeRateEntry {
  ageBand?: string | null;
  ratePerUnit: number | null;
}

export type WizardRateTable =
  | { mode: "tier_rates"; entries: TierRateEntry[] }
  | {
      mode: "age_tier_rates";
      ageBandType?: AgeBandType;
      entries: AgeTierRateEntry[];
    }
  | {
      mode: "volume_per_thousand";
      rateBasis?: LifeDisabilityRateBasis;
      rateType?: LifeDisabilityRateType;
      ageBandType?: AgeBandType;
      entries: VolumeRateEntry[];
    };

const DEFAULT_AGE_BAND_TYPE: AgeBandType = "five_year";

function buildAgeTierEntries(bandType: AgeBandType): AgeTierRateEntry[] {
  const entries: AgeTierRateEntry[] = [];
  for (const band of AGE_BAND_PRESETS[bandType]) {
    for (const t of TIERS) {
      entries.push({ ageBand: band, tier: t.key, monthlyRate: null });
    }
  }
  return entries;
}

function buildVolumeBandedEntries(bandType: AgeBandType): VolumeRateEntry[] {
  return AGE_BAND_PRESETS[bandType].map((band) => ({
    ageBand: band,
    ratePerUnit: null,
  }));
}

export function emptyRateTable(
  mode: WizardRateMode,
  internalPlanType?: string | null,
): WizardRateTable {
  if (mode === "tier_rates") {
    return {
      mode: "tier_rates",
      entries: TIERS.map((t) => ({ tier: t.key, monthlyRate: null })),
    };
  }
  if (mode === "age_tier_rates") {
    return {
      mode: "age_tier_rates",
      ageBandType: DEFAULT_AGE_BAND_TYPE,
      entries: buildAgeTierEntries(DEFAULT_AGE_BAND_TYPE),
    };
  }
  const defaults = getLifeDisabilityRateDefaults(internalPlanType);
  return {
    mode: "volume_per_thousand",
    rateBasis: defaults.rateBasis,
    rateType: defaults.rateType,
    ageBandType: DEFAULT_AGE_BAND_TYPE,
    entries:
      defaults.rateType === "tiered"
        ? buildVolumeBandedEntries(DEFAULT_AGE_BAND_TYPE)
        : [{ ageBand: null, ratePerUnit: null }],
  };
}

interface Props {
  mode: WizardRateMode;
  rateTable: WizardRateTable | undefined;
  onChange: (next: WizardRateTable) => void;
  disabled?: boolean;
  internalPlanType?: string | null;
}

function parseNum(v: string): number | null {
  const cleaned = v.replace(/[^\d.-]/g, "");
  if (!cleaned) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

function fmt(n: number | null): string {
  return n == null ? "" : String(n);
}

/**
 * Numeric input that holds its typed-string state locally so users can type
 * mid-decimal sequences like "0." or "0.4" without the controlled-number
 * pattern echoing back a parsed value and eating the trailing dot.
 *
 * Re-syncs from `value` only when the prop genuinely changes to something
 * different from what the local string parses to (e.g., parent overwrites
 * the value via auto-derive or a sibling tier).
 */
function RateInput({
  value,
  onChange,
  placeholder,
  disabled,
  className,
}: {
  value: number | null;
  onChange: (n: number | null) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}) {
  const [str, setStr] = useState<string>(fmt(value));
  useEffect(() => {
    // Only re-sync when the external value disagrees with what our string
    // parses to. This preserves in-progress states like "0." during typing.
    const parsed = parseNum(str);
    if (parsed !== value) {
      setStr(fmt(value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return (
    <Input
      type="text"
      inputMode="decimal"
      placeholder={placeholder}
      disabled={disabled}
      className={className}
      value={str}
      onChange={(e) => {
        const raw = e.target.value;
        setStr(raw);
        onChange(parseNum(raw));
      }}
    />
  );
}

export function RateTableEditor({
  mode,
  rateTable,
  onChange,
  disabled,
  internalPlanType,
}: Props) {
  const table =
    rateTable && rateTable.mode === mode
      ? rateTable
      : emptyRateTable(mode, internalPlanType);

  if (mode === "tier_rates" && table.mode === "tier_rates") {
    return (
      <div className="space-y-2">
        <Label className="text-xs">Monthly rate by coverage tier</Label>
        <p className="text-[11px] text-muted-foreground">
          Leave a tier blank to auto-import the rate from the source
          enrollment data, or enter a value to override.
        </p>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {TIERS.map((t) => {
            const entry = table.entries.find((e) => e.tier === t.key);
            return (
              <div key={t.key} className="space-y-1">
                <Label className="text-[10px] text-muted-foreground">
                  {t.label}
                </Label>
                <RateInput
                  placeholder="0.00"
                  value={entry?.monthlyRate ?? null}
                  disabled={disabled}
                  onChange={(v) => {
                    const next = {
                      mode: "tier_rates" as const,
                      entries: TIERS.map((tt) => ({
                        tier: tt.key,
                        monthlyRate:
                          tt.key === t.key
                            ? v
                            : table.entries.find((x) => x.tier === tt.key)
                                ?.monthlyRate ?? null,
                      })),
                    };
                    onChange(next);
                  }}
                />
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Age × tier matrix — manual fallback when the library doesn't have the
  // plan. Broker picks an Age Band Structure ("5 Year" / "10 Year") and fills
  // one rate per (band, tier) cell. Mirrors the plan-detail page's Age Band +
  // Tier Rate Matrix exactly so the wizard and post-import edit screens share
  // the same UX. Used when `plan.manualEntry === true`; otherwise PlansStep
  // renders `<MatchPanel>` for the library-driven path.
  if (mode === "age_tier_rates" && table.mode === "age_tier_rates") {
    const ageEntries: AgeTierRateEntry[] = table.entries;
    const savedBands = Array.from(new Set(ageEntries.map((e) => e.ageBand)));
    // Infer the band type from saved data: if every saved band is in the
    // ten_year preset, treat it as ten_year. Otherwise five_year. Explicit
    // `ageBandType` on the table wins.
    const inferredBandType: AgeBandType =
      table.ageBandType ??
      (savedBands.length > 0 &&
      savedBands.every((b) =>
        (AGE_BAND_PRESETS.ten_year as readonly string[]).includes(b),
      ) &&
      !savedBands.every((b) =>
        (AGE_BAND_PRESETS.five_year as readonly string[]).includes(b),
      )
        ? "ten_year"
        : DEFAULT_AGE_BAND_TYPE);
    const presetBands = AGE_BAND_PRESETS[inferredBandType];
    const presetSet = new Set(presetBands);
    const extraBands = savedBands
      .filter((b) => !presetSet.has(b))
      .sort((a, b) => parseAgeBandStart(a) - parseAgeBandStart(b));
    const displayBands = [...presetBands, ...extraBands];

    const getCell = (band: string, tier: Tier): number | null =>
      ageEntries.find((e) => e.ageBand === band && e.tier === tier)
        ?.monthlyRate ?? null;
    const setCell = (band: string, tier: Tier, val: number | null) => {
      const entries = ageEntries.slice();
      const idx = entries.findIndex(
        (e) => e.ageBand === band && e.tier === tier,
      );
      if (idx >= 0) entries[idx] = { ...entries[idx], monthlyRate: val };
      else entries.push({ ageBand: band, tier, monthlyRate: val });
      onChange({
        mode: "age_tier_rates",
        ageBandType: inferredBandType,
        entries,
      });
    };
    const changeBandType = (next: AgeBandType) => {
      if (next === inferredBandType) return;
      // Rebuild entries for the new preset, preserving rates for any
      // (band, tier) pair whose band exists in the new preset. Bands that
      // don't carry over are dropped (matches PlanRates.tsx behavior).
      const nextBands = AGE_BAND_PRESETS[next];
      const entries: AgeTierRateEntry[] = [];
      for (const band of nextBands) {
        for (const t of TIERS) {
          const existing = ageEntries.find(
            (e) => e.ageBand === band && e.tier === t.key,
          );
          entries.push({
            ageBand: band,
            tier: t.key,
            monthlyRate: existing?.monthlyRate ?? null,
          });
        }
      }
      onChange({ mode: "age_tier_rates", ageBandType: next, entries });
    };

    return (
      <div className="space-y-3">
        <div className="space-y-1">
          <Label className="text-xs">Age Band Structure</Label>
          <Select
            value={inferredBandType}
            onValueChange={(val) => changeBandType(val as AgeBandType)}
            disabled={disabled}
          >
            <SelectTrigger className="h-8 w-48 text-xs">
              <SelectValue placeholder="Select age band structure" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="five_year">
                {AGE_BAND_TYPE_LABELS.five_year}
              </SelectItem>
              <SelectItem value="ten_year">
                {AGE_BAND_TYPE_LABELS.ten_year}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Monthly rate by age band × tier</Label>
          <div className="overflow-x-auto rounded border">
            <table className="w-full text-xs">
              <thead className="bg-muted/40">
                <tr>
                  <th className="p-2 text-left font-medium">Age Band</th>
                  {TIERS.map((t) => (
                    <th
                      key={t.key}
                      className="p-2 text-center font-medium"
                    >
                      {t.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {displayBands.map((band) => (
                  <tr key={band} className="border-t">
                    <td className="p-2 font-medium">{band}</td>
                    {TIERS.map((t) => (
                      <td key={t.key} className="p-1">
                        <RateInput
                          placeholder="0.00"
                          value={getCell(band, t.key)}
                          disabled={disabled}
                          onChange={(v) => setCell(band, t.key, v)}
                          className="h-7 text-xs"
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  // Volume rate editor (life / disability / etc.). Mirrors the plan-detail
  // page's "Life & Disability" Rate Basis + Rate Type controls so brokers
  // see the same picker on both surfaces. Tiered mode reuses the same
  // 5/10-year age-band presets as the age × tier matrix.
  if (mode === "volume_per_thousand" && table.mode === "volume_per_thousand") {
    const defaults = getLifeDisabilityRateDefaults(internalPlanType);
    const rateBasis: LifeDisabilityRateBasis = table.rateBasis ?? defaults.rateBasis;
    const rateType: LifeDisabilityRateType = table.rateType ?? defaults.rateType;
    const entries: VolumeRateEntry[] = table.entries;
    const composite = entries.find((e) => !e.ageBand);
    const banded = entries.filter((e) => !!e.ageBand);
    const savedBands = banded.map((e) => e.ageBand as string);

    // Same inference pattern as age × tier: detect 10-year preset when every
    // saved band sits inside it; otherwise default to 5-year.
    const inferredBandType: AgeBandType =
      table.ageBandType ??
      (savedBands.length > 0 &&
      savedBands.every((b) =>
        (AGE_BAND_PRESETS.ten_year as readonly string[]).includes(b),
      ) &&
      !savedBands.every((b) =>
        (AGE_BAND_PRESETS.five_year as readonly string[]).includes(b),
      )
        ? "ten_year"
        : DEFAULT_AGE_BAND_TYPE);

    const presetBands = AGE_BAND_PRESETS[inferredBandType];
    const presetSet = new Set(presetBands);
    const extraBands = savedBands
      .filter((b) => !presetSet.has(b))
      .sort((a, b) => parseAgeBandStart(a) - parseAgeBandStart(b));
    const displayBands = [...presetBands, ...extraBands];

    const getBandRate = (band: string): number | null =>
      banded.find((e) => e.ageBand === band)?.ratePerUnit ?? null;

    const setBandRate = (band: string, val: number | null) => {
      const nextBanded = banded.slice();
      const idx = nextBanded.findIndex((e) => e.ageBand === band);
      if (idx >= 0) nextBanded[idx] = { ageBand: band, ratePerUnit: val };
      else nextBanded.push({ ageBand: band, ratePerUnit: val });
      onChange({
        mode: "volume_per_thousand",
        rateBasis,
        rateType,
        ageBandType: inferredBandType,
        entries: composite ? [composite, ...nextBanded] : nextBanded,
      });
    };

    const setComposite = (val: number | null) =>
      onChange({
        mode: "volume_per_thousand",
        rateBasis,
        rateType,
        ageBandType: inferredBandType,
        entries: [{ ageBand: null, ratePerUnit: val }, ...banded],
      });

    const changeRateBasis = (next: LifeDisabilityRateBasis) => {
      if (next === rateBasis) return;
      onChange({
        mode: "volume_per_thousand",
        rateBasis: next,
        rateType,
        ageBandType: inferredBandType,
        entries,
      });
    };

    const changeRateType = (next: LifeDisabilityRateType) => {
      if (next === rateType) return;
      if (next === "tiered") {
        // composite -> tiered: seed preset rows × null, preserve composite in
        // state so the user can undo by flipping back.
        const seeded: VolumeRateEntry[] = presetBands.map((band) => ({
          ageBand: band,
          ratePerUnit: getBandRate(band),
        }));
        onChange({
          mode: "volume_per_thousand",
          rateBasis,
          rateType: "tiered",
          ageBandType: inferredBandType,
          entries: composite ? [composite, ...seeded] : seeded,
        });
      } else {
        // tiered -> composite: collapse to a single entry. Keep the existing
        // composite rate if broker had typed one; otherwise borrow the first
        // non-null banded rate so they don't lose their work entirely.
        const compositeRate =
          composite?.ratePerUnit ??
          banded.find((e) => e.ratePerUnit != null)?.ratePerUnit ??
          null;
        onChange({
          mode: "volume_per_thousand",
          rateBasis,
          rateType: "composite",
          ageBandType: inferredBandType,
          entries: [{ ageBand: null, ratePerUnit: compositeRate }],
        });
      }
    };

    const changeBandType = (next: AgeBandType) => {
      if (next === inferredBandType) return;
      const nextBands = AGE_BAND_PRESETS[next];
      const nextBanded: VolumeRateEntry[] = nextBands.map((band) => ({
        ageBand: band,
        ratePerUnit: getBandRate(band),
      }));
      onChange({
        mode: "volume_per_thousand",
        rateBasis,
        rateType,
        ageBandType: next,
        entries: composite ? [composite, ...nextBanded] : nextBanded,
      });
    };

    return (
      <div className="space-y-3">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="space-y-1">
            <Label className="text-xs">Rate Basis</Label>
            <Select
              value={rateBasis}
              onValueChange={(val) =>
                changeRateBasis(val as LifeDisabilityRateBasis)
              }
              disabled={disabled}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="benefit_amount">
                  {RATE_BASIS_LABELS.benefit_amount}
                </SelectItem>
                <SelectItem value="earnings_amount">
                  {RATE_BASIS_LABELS.earnings_amount}
                </SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[10px] text-muted-foreground">
              What the rate is calculated against.
            </p>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Rate Type</Label>
            <Select
              value={rateType}
              onValueChange={(val) =>
                changeRateType(val as LifeDisabilityRateType)
              }
              disabled={disabled}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="composite">
                  {RATE_TYPE_LABELS.composite}
                </SelectItem>
                <SelectItem value="tiered">
                  {RATE_TYPE_LABELS.tiered}
                </SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[10px] text-muted-foreground">
              Composite = single rate; Tiered = age-banded rates.
            </p>
          </div>
        </div>

        {rateType === "composite" ? (
          <div className="space-y-1">
            <Label className="text-[10px] text-muted-foreground">
              {rateBasis === "earnings_amount"
                ? "Composite rate (per $100 of earnings)"
                : "Composite rate (per $1,000 of coverage)"}
            </Label>
            <RateInput
              placeholder="0.0000"
              value={composite?.ratePerUnit ?? null}
              disabled={disabled}
              onChange={(v) => setComposite(v)}
            />
          </div>
        ) : (
          <div className="space-y-2">
            <div className="space-y-1">
              <Label className="text-xs">Age Band Structure</Label>
              <Select
                value={inferredBandType}
                onValueChange={(val) => changeBandType(val as AgeBandType)}
                disabled={disabled}
              >
                <SelectTrigger className="h-8 w-48 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="five_year">
                    {AGE_BAND_TYPE_LABELS.five_year}
                  </SelectItem>
                  <SelectItem value="ten_year">
                    {AGE_BAND_TYPE_LABELS.ten_year}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="overflow-x-auto rounded border">
              <table className="w-full text-xs">
                <thead className="bg-muted/40">
                  <tr>
                    <th className="p-2 text-left font-medium">Age Band</th>
                    <th className="p-2 text-center font-medium">
                      {rateBasis === "earnings_amount"
                        ? "Rate per $100"
                        : "Rate per $1,000"}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {displayBands.map((band) => (
                    <tr key={band} className="border-t">
                      <td className="p-2 font-medium">{band}</td>
                      <td className="p-1">
                        <RateInput
                          placeholder="0.0000"
                          value={getBandRate(band)}
                          disabled={disabled}
                          onChange={(v) => setBandRate(band, v)}
                          className="h-7 text-xs"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  }

  return null;
}

/**
 * Returns true when the rateTable is "filled in enough" to commit. Used by
 * PlansStep's confirm gate.
 *
 * Tier rates: require ALL four tiers (EE / EE+Spouse / EE+Children /
 * Family). A partial tier table is broken data — employees in the
 * missing tier(s) can't be priced correctly. Surface the gap as a
 * warning before confirm rather than letting it through.
 *
 * Age × tier matrix + volume: at least one cell is enough; carrier
 * rate sheets vary in completeness and we trust the broker to add
 * what they have.
 */
export function isRateTableComplete(table: WizardRateTable | undefined): boolean {
  if (!table) return false;
  if (table.mode === "tier_rates") {
    return TIERS.every((t) => {
      const entry = table.entries.find((e) => e.tier === t.key);
      return entry?.monthlyRate != null && entry.monthlyRate >= 0;
    });
  }
  if (table.mode === "age_tier_rates") {
    return table.entries.some(
      (e) => e.monthlyRate != null && e.monthlyRate >= 0,
    );
  }
  if (table.mode === "volume_per_thousand") {
    return table.entries.some(
      (e) => e.ratePerUnit != null && e.ratePerUnit >= 0,
    );
  }
  return false;
}

/**
 * For a tier_rates table, returns the tier labels that are still empty.
 * Lets the warning panel point the broker at the specific gaps ("Family
 * is missing") instead of a generic "rate table" complaint.
 */
export function missingTierLabels(table: WizardRateTable | undefined): string[] {
  if (!table || table.mode !== "tier_rates") return [];
  return TIERS.filter((t) => {
    const entry = table.entries.find((e) => e.tier === t.key);
    return entry?.monthlyRate == null;
  }).map((t) => t.label);
}

export interface ContributionEntry {
  tier: string;
  employer: number | null;
  employee: number | null;
}

/**
 * For tier_rates plans, true when every coverage tier (EE Only / EE + Spouse
 * / EE + Children / Family) has BOTH an employer and an employee dollar
 * amount set. 0 counts as set — that's a valid split (carrier pays 100% one
 * way or the other). Confirm writes one plan_contributions row per tier; if
 * a tier is blank, downstream pricing breaks for employees in that tier.
 */
export function isContributionsComplete(
  contributions: ContributionEntry[] | undefined,
): boolean {
  if (!contributions || contributions.length === 0) return false;
  return TIERS.every((t) => {
    const c = contributions.find((x) => x.tier === t.key);
    return c != null && c.employer != null && c.employee != null;
  });
}

export function missingContributionTierLabels(
  contributions: ContributionEntry[] | undefined,
): string[] {
  return TIERS.filter((t) => {
    const c = contributions?.find((x) => x.tier === t.key);
    return !c || c.employer == null || c.employee == null;
  }).map((t) => t.label);
}
